import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ArrowLeft } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

import { SupplierAPI } from "@/lib/api";
import { extractError } from "@/lib/api";

export default function SuppliersPage() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await SupplierAPI.list({ q: q || undefined, page_size: 200 });
            setItems(d.items || []);
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="suppliers-page">
            <header className="flex items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Procurement</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">Suppliers</h1>
                    <p className="mt-1 text-sm text-zinc-500">Vendors who supply parts. GSTIN-validated and tied to purchase orders.</p>
                </div>
                <Link to="/suppliers/new">
                    <Button data-testid="supplier-new-btn"><Plus className="mr-1 h-4 w-4" />New supplier</Button>
                </Link>
            </header>

            <div className="flex items-center gap-2">
                <div className="relative max-w-md flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                        data-testid="supplier-search-input"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, code, GSTIN, phone…"
                        className="pl-9"
                    />
                </div>
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="suppliers-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Code</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Name</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">GSTIN</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Phone</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Terms</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Status</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((s) => (
                            <TableRow
                                key={s.id}
                                data-testid={`supplier-row-${s.id}`}
                                className="cursor-pointer"
                                onClick={() => (window.location.href = `/suppliers/${s.id}`)}
                            >
                                <TableCell className="font-mono">{s.supplier_code}</TableCell>
                                <TableCell className="font-medium">{s.name}</TableCell>
                                <TableCell className="font-mono text-xs">{s.gstin || "—"}</TableCell>
                                <TableCell className="font-mono text-xs">{s.phone}</TableCell>
                                <TableCell className="font-mono text-xs">{s.payment_terms_days}d</TableCell>
                                <TableCell>
                                    {s.is_active
                                        ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">Active</Badge>
                                        : <Badge variant="outline">Inactive</Badge>}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">No suppliers yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export function SupplierNewPage() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "", contact_person: "", phone: "", email: "",
        gstin: "", payment_terms_days: 30, notes: "",
        address: { line1: "", city: "", state_code: "KA", pincode: "", country: "IN" },
    });
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
        setSubmitting(true);
        try {
            const payload = { ...form, gstin: form.gstin.trim() || null };
            const created = await SupplierAPI.create(payload);
            toast.success(`Created supplier ${created.supplier_code}`);
            nav(`/suppliers/${created.id}`);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="mx-auto max-w-3xl space-y-6" data-testid="supplier-new-page">
            <Link to="/suppliers" className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900">
                <ArrowLeft className="mr-1 h-4 w-4" />Back to suppliers
            </Link>
            <header>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">New supplier</h1>
                <p className="mt-1 text-sm text-zinc-500">Code is auto-allocated. GSTIN validated against Indian checksum rules.</p>
            </header>
            <Card>
                <CardContent className="space-y-4 p-5">
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Name *"><Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Contact person"><Input data-testid="supplier-contact-input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
                        <Field label="Phone *"><Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+919xxxxxxxxx" /></Field>
                        <Field label="Email"><Input data-testid="supplier-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                        <Field label="GSTIN"><Input data-testid="supplier-gstin-input" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1Z5" /></Field>
                        <Field label="Payment terms (days)"><Input data-testid="supplier-terms-input" type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })} /></Field>
                    </div>
                    <Field label="Address — Line 1"><Input value={form.address.line1} onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value } })} /></Field>
                    <div className="grid gap-4 md:grid-cols-3">
                        <Field label="City"><Input value={form.address.city} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} /></Field>
                        <Field label="State code"><Input value={form.address.state_code} onChange={(e) => setForm({ ...form, address: { ...form.address, state_code: e.target.value.toUpperCase() } })} /></Field>
                        <Field label="Pincode"><Input value={form.address.pincode} onChange={(e) => setForm({ ...form, address: { ...form.address, pincode: e.target.value } })} /></Field>
                    </div>
                    <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => nav("/suppliers")}>Cancel</Button>
                        <Button onClick={submit} disabled={submitting} data-testid="supplier-submit-btn">
                            {submitting ? "Creating…" : "Create supplier"}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export function SupplierDetailPage() {
    const id = window.location.pathname.split("/").pop();
    const [s, setS] = useState(null);
    useEffect(() => { SupplierAPI.get(id).then(setS); }, [id]);
    if (!s) return <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>;
    return (
        <div className="mx-auto max-w-3xl space-y-6" data-testid="supplier-detail-page">
            <Link to="/suppliers" className="inline-flex items-center text-sm text-zinc-500"><ArrowLeft className="mr-1 h-4 w-4" />Back</Link>
            <header className="flex items-end justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">{s.supplier_code}</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">{s.name}</h1>
                </div>
                {s.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}
            </header>
            <Card>
                <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
                <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                    <KV label="Contact" value={s.contact_person || "—"} />
                    <KV label="Phone" value={s.phone} />
                    <KV label="Email" value={s.email || "—"} />
                    <KV label="GSTIN" value={<span className="font-mono">{s.gstin || "—"}</span>} />
                    <KV label="Payment terms" value={`${s.payment_terms_days} days`} />
                    <KV label="Open POs (any status)" value={s.recent_po_count ?? 0} />
                </CardContent>
            </Card>
            <Card>
                <CardHeader><CardTitle>Address</CardTitle></CardHeader>
                <CardContent className="text-sm text-zinc-600 dark:text-zinc-400">
                    <div>{s.address?.line1}</div>
                    <div>{s.address?.city} {s.address?.state_code} {s.address?.pincode}</div>
                    <div>{s.address?.country}</div>
                </CardContent>
            </Card>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}

function KV({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
            <div className="mt-0.5 font-medium text-zinc-900 dark:text-zinc-100">{value}</div>
        </div>
    );
}
